import React, { Component } from 'react';
import type { ReactNode } from 'react';
import { captureError } from '../services/errorTracking';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  eventId?: string;
}

/**
 * Error boundary component for graceful crash recovery.
 *
 * Catches errors in the React component tree, reports them to Sentry,
 * and displays a luxury-themed fallback UI with a reload button.
 *
 * Note: Error boundaries must be class components as per React documentation.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Report error to Sentry
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    try {
      captureError(error, {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
      });
    } catch (sentryError) {
      console.error('Failed to report error to Sentry:', sentryError);
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          data-testid="error-boundary-fallback"
          className="min-h-screen bg-brand-cream flex items-center justify-center p-6"
        >
          <div className="max-w-md w-full text-center space-y-6">
            <h1 className="font-serif text-4xl text-brand-navy mb-4">
              Something went wrong
            </h1>

            <p className="text-brand-navy/70 text-lg mb-8">
              We're sorry for the inconvenience. Please try reloading the page.
            </p>

            <button
              onClick={this.handleReload}
              className="bg-brand-gold hover:bg-brand-gold-dark text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              Reload Page
            </button>

            {this.state.eventId && (
              <p className="text-sm text-gray-500 mt-6">
                Error ID: {this.state.eventId}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
